import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Container,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import axios from "axios";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

const ProductEdit = () => {
  const user = useSelector((state) => state.authentication.user);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [diamonds, setDiamonds] = useState([{}]);
  const [colorStones, setColorStones] = useState([{}]);
  const [golds, setGolds] = useState([{}]);
  const [prod_num, setprod_num] = useState(0);
  const [image, setimage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [ornaments, setOrnaments] = useState([]);

  const { Tag_No } = useParams();
  const navigate = useNavigate();

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`/api/product/${Tag_No}/`);
      const product = response.data;

      setFormData(product);
      initializeGroups(product); // FIXED: pass the product, not the whole response
      setLoading(false);
    } catch (error) {
      console.error("Error fetching product:", error);
      setLoading(false);
    }
  };

  const initializeGroups = (data) => {
    const getFilledCount = (prefix) => {
      const matchingKeys = Object.keys(data).filter((k) =>
        k.startsWith(prefix),
      );

      const usedIndexes = new Set();

      matchingKeys.forEach((key) => {
        const match = key.match(/^([A-Za-z]+)_(\d{2})$/); // Matches e.g. Diamond_01
        if (match) {
          const index = parseInt(match[2], 10);
          const value = data[key];
          if (value !== null && value !== "" && value !== "null") {
            usedIndexes.add(index);
          }
        }
      });

      return Math.max(...usedIndexes, 0);
    };

    const diamondCount = getFilledCount("Diamond");
    const colorStoneCount = getFilledCount("CStone");
    const goldCount = getFilledCount("Gold");

    setDiamonds(Array(diamondCount).fill({}));
    setColorStones(Array(colorStoneCount).fill({}));
    setGolds(Array(goldCount).fill({}));
  };

  useEffect(() => {
    fetchProduct();
  }, [Tag_No]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedData = { ...formData, [name]: value };

    const rateWeightMatch = name.match(
      /^(D|CS|G)(\d+)_([CS])?_?(Rate|Weight)$/,
    );
    if (rateWeightMatch) {
      const [_, prefix, index] = rateWeightMatch;
      const base = `${prefix}${index}`;
      const weightField = `${base}_Weight`;
      const cRateField = `${base}_C_Rate`;
      const cAmountField = `${base}_C_Amount`;
      const sRateField = `${base}_S_Rate`;
      const sAmountField = `${base}_S_Amount`;

      const weight = parseFloat(
        name === weightField ? value : updatedData[weightField],
      );
      const cRate = parseFloat(
        name === cRateField ? value : updatedData[cRateField],
      );
      const sRate = parseFloat(
        name === sRateField ? value : updatedData[sRateField],
      );

      if (!isNaN(weight) && !isNaN(cRate)) {
        updatedData[cAmountField] = Math.ceil(weight * cRate);
      }
      if (!isNaN(weight) && !isNaN(sRate)) {
        updatedData[sAmountField] = Math.ceil(weight * sRate);
      }

      const g1 = parseFloat(
        name === "G1_Weight" ? value : updatedData["G1_Weight"],
      );
      const g2 = parseFloat(
        name === "G2_Weight" ? value : updatedData["G2_Weight"],
      );
      if (!isNaN(g1) || !isNaN(g2)) {
        const total = (g1 || 0) + (g2 || 0);
        updatedData["S_Making"] = Math.ceil(2000 * total);
      }
    }

    setFormData(updatedData);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formField = new FormData();
      formField.append("user", user.user_id);

      Object.entries(formData).forEach(([key, value]) => {
        if (
          value !== null &&
          value !== undefined &&
          value !== "null" &&
          value !== "" &&
          key !== "image"
        ) {
          formField.append(key, value);
        }
      });

      if (image !== null) {
        formField.append("image", image);
      }

      await axios.patch(`/api/product/${Tag_No}/`, formField);
      alert("Product updated successfully.");
      navigate(`/p/${Tag_No}`);
    } catch (err) {
      console.error("Update failed", err);
      alert("Product updated successfully.");
    }
    setSubmitting(false);
  };

  const getResponse = async () => {
    try {
      const res = await axios.get(`/api/products/`);

      const ornamentSet = new Set(
        res.data.map((p) => p.Ornament).filter(Boolean),
      );
      setOrnaments(["All", ...Array.from(ornamentSet).sort()]);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  useEffect(() => {
    getResponse();
  }, []);

  const handleAdd = (type) => {
    if (type === "diamond" && diamonds.length < 10)
      setDiamonds([...diamonds, {}]);
    if (type === "colorStone" && colorStones.length < 5)
      setColorStones([...colorStones, {}]);
    if (type === "gold" && golds.length < 2) setGolds([...golds, {}]);
  };

  const handleRemove = (type, index) => {
    if (type === "diamond") setDiamonds(diamonds.filter((_, i) => i !== index));
    if (type === "colorStone")
      setColorStones(colorStones.filter((_, i) => i !== index));
    if (type === "gold") setGolds(golds.filter((_, i) => i !== index));
  };

  const renderGroupedFields = (fields, layout = [], readOnlyFields = []) => {
    const rows = [];
    let index = 0;
    layout.forEach((colsPerRow) => {
      const rowFields = fields.slice(index, index + colsPerRow);
      rows.push(
        <Row className="mb-3" key={index}>
          {rowFields.map((field) => {
            const isReadOnly = readOnlyFields.includes(field);
            const isOrnament = field === "Ornament";
            const isGold = field === "Gold_01" || field === "Gold_02";

            return (
              <Col key={field}>
                <Form.Group controlId={field}>
                  <Form.Label>{field.replace(/_/g, " ")}</Form.Label>
                  {isOrnament ? (
                    <Form.Select
                      name={field}
                      value={formData[field] || ""}
                      onChange={handleChange}
                      disabled={isReadOnly}
                    >
                      <option value="">Select Ornament</option>
                      {ornaments.map((ornament, idx) => (
                        <option key={idx} value={ornament}>
                          {ornament}
                        </option>
                      ))}
                    </Form.Select>
                  ) : isGold ? (
                    <Form.Select
                      name={field}
                      value={formData[field] || ""}
                      onChange={handleChange}
                      disabled={isReadOnly}
                    >
                      <option value="">Select Gold Type</option>
                      <option value="GOLD - 22CT">GOLD - 22CT</option>
                      <option value="GOLD - 18CT">GOLD - 18CT</option>
                      <option value="GOLD - 14CT">GOLD - 14CT</option>
                    </Form.Select>
                  ) : (
                    <Form.Control
                      type="text"
                      name={field}
                      value={formData[field] || ""}
                      onChange={handleChange}
                      readOnly={isReadOnly}
                    />
                  )}
                </Form.Group>
              </Col>
            );
          })}
        </Row>,
      );
      index += colsPerRow;
    });
    return rows;
  };

  const renderFields = (
    label,
    fields,
    index = null,
    type = null,
    readOnlyFields = [],
  ) => {
    let layout =
      label.includes("Diamond") || label.includes("Color Stone")
        ? [3, 2, 2]
        : [2, 2, 2];
    return (
      <div className="mb-4" key={index}>
        <h5>{label}</h5>
        {renderGroupedFields(fields, layout, readOnlyFields)}
        {type && (
          <Button
            variant="danger"
            className="mb-3"
            onClick={() => handleRemove(type, index)}
          >
            Remove
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container style={{ maxWidth: "95%", padding: "2vh 0" }}>
      <h2>Edit Product: {Tag_No}</h2>
      <Form>
        {renderFields(
          "General Info",
          ["Tag_No", "Ornament", "Certificate_No"],
          null,
          null,
          ["Tag_No"], // this field will now be read-only
        )}

        <Row className="mb-3">
          <Col>
            <Form.Group controlId="Productimage">
              <Form.Label>Product image</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={(e) => setimage(e.target.files[0] || null)}
              />
            </Form.Group>
          </Col>
        </Row>

        {diamonds.map((_, i) =>
          renderFields(
            `Diamond ${i + 1}`,
            [
              `Diamond_0${i + 1}`,
              `D${i + 1}_Pcs`,
              `D${i + 1}_Weight`,
              `D${i + 1}_C_Rate`,
              `D${i + 1}_C_Amount`,
              `D${i + 1}_S_Rate`,
              `D${i + 1}_S_Amount`,
            ],
            i,
            "diamond",
          ),
        )}
        <Button onClick={() => handleAdd("diamond")} className="mb-3">
          Add Diamond
        </Button>

        {colorStones.map((_, i) =>
          renderFields(
            `Color Stone ${i + 1}`,
            [
              `CStone_0${i + 1}`,
              `CS${i + 1}_Pcs`,
              `CS${i + 1}_Weight`,
              `CS${i + 1}_C_Rate`,
              `CS${i + 1}_C_Amount`,
              `CS${i + 1}_S_Rate`,
              `CS${i + 1}_S_Amount`,
            ],
            i,
            "colorStone",
          ),
        )}
        <Button onClick={() => handleAdd("colorStone")} className="mb-3">
          Add Colour Stone
        </Button>

        {golds.map((_, i) =>
          renderFields(
            `Gold ${i + 1}`,
            [
              `Gold_0${i + 1}`,
              `G${i + 1}_Weight`,
              `G${i + 1}_C_Rate`,
              `G${i + 1}_C_Amount`,
              `G${i + 1}_S_Rate`,
              `G${i + 1}_S_Amount`,
            ],
            i,
            "gold",
          ),
        )}
        <Button onClick={() => handleAdd("gold")} className="mb-3">
          Add Gold
        </Button>

        {renderFields("Making Charges", ["C_Making", "S_Making"])}

        {submitting ? (
          <OverlayTrigger
            overlay={
              <Tooltip id="tooltip-disabled">Submitting Please Wait</Tooltip>
            }
          >
            <Button disabled variant="primary">
              Updating
            </Button>
          </OverlayTrigger>
        ) : (
          <Button variant="primary" onClick={handleUpdate}>
            Update Product
          </Button>
        )}
      </Form>
    </Container>
  );
};

export default ProductEdit;
